/**
 * Smart Section Manager for Cart Components
 * Optimizes section rendering by implementing:
 * - Content caching and differential updates
 * - Smart section loading (only update what changed)
 * - Shared DOMParser instance
 * - Performance monitoring
 */
class CartSectionManager {
  constructor() {
    this.sectionCache = new Map();
    this.parser = new DOMParser();
    this.lastCartState = null;
    this.performanceMetrics = {
      totalUpdates: 0,
      skippedUpdates: 0,
      cacheHits: 0
    };
  }

  /**
   * Smart render that only updates sections that have actually changed
   */
  smartRender(component, parsedState, sectionsToRender) {
    const startTime = performance.now();
    
    if (window.cartPerformanceDebug) {
      console.log(`🎯 Processing ${sectionsToRender.length} sections`);
    }
    
    // Track which sections actually need updates
    const sectionsToUpdate = this.identifyChangedSections(parsedState, sectionsToRender);
    
    if (sectionsToUpdate.length === 0) {
      this.performanceMetrics.skippedUpdates++;
      return;
    }
    
    // Batch update only the changed sections
    this.batchUpdateSections(sectionsToUpdate, parsedState);
    
    // Update cache with new content
    this.updateSectionCache(parsedState, sectionsToRender);
    
    const endTime = performance.now();
    this.performanceMetrics.totalUpdates++;
    
    if (window.cartPerformanceDebug) {
      console.log(`⚡ Updated ${sectionsToUpdate.length} sections in ${(endTime - startTime).toFixed(0)}ms`);
    }
  }

  /**
   * Identify which sections have actually changed content
   */
  identifyChangedSections(parsedState, sectionsToRender) {
    const changedSections = [];
    
    sectionsToRender.forEach(section => {
      const sectionId = section.id;
      const newContent = parsedState.sections[sectionId];
      
      if (!newContent) return;
      
      // Check if we have cached content for this section
      const cachedData = this.sectionCache.get(sectionId);
      
      if (!cachedData) {
        // No cache entry - this section needs updating
        changedSections.push(section);
        if (window.cartPerformanceDebug) {
          console.log(`Section ${sectionId}: No cache entry (first render)`);
        }
        return;
      }
      
      // Compare meaningful content instead of raw HTML
      const newContentHash = this.getContentHash(newContent, section);
      const cachedContentHash = cachedData.hash;
      
      if (newContentHash !== cachedContentHash) {
        changedSections.push(section);
        if (window.cartPerformanceDebug) {
          console.log(`Section ${sectionId}: Content changed`);
          console.log('Old hash:', cachedContentHash);
          console.log('New hash:', newContentHash);
        }
      } else {
        this.performanceMetrics.cacheHits++;
        if (window.cartPerformanceDebug) {
          console.log(`Section ${sectionId}: Cache hit - content unchanged`);
        }
      }
    });
    
    return changedSections;
  }

  /**
   * Batch update multiple sections efficiently
   */
  batchUpdateSections(sectionsToUpdate, parsedState) {
    if (sectionsToUpdate.length === 0) return;
    
    // Group updates by element to minimize DOM queries
    const elementUpdates = new Map();
    
    sectionsToUpdate.forEach(section => {
      let element;
      let selectorToUse = section.selector;
      
      // Special handling for cart-icon-bubble to target the correct element
      if (section.id === 'cart-icon-bubble') {
        element = document.getElementById('cart-icon-bubble');
        selectorToUse = '.shopify-section'; // Extract content from the shopify section wrapper
      } else if (section.id === 'main-cart-items') {
        element = document.getElementById('main-cart-items');
        selectorToUse = '.js-contents'; // Extract just the table contents, avoiding the outer wrapper duplication
      } else if (section.id === 'main-cart-footer') {
        // Target the parent shopify section instead of the inner main-cart-footer
        const mainCartFooter = document.getElementById('main-cart-footer');
        element = mainCartFooter ? mainCartFooter.closest('.shopify-section') : null;
        selectorToUse = '.shopify-section'; // Extract the full shopify section to get all cart totals content
      } else if (section.id === 'cart-items') {
        element = document.querySelector('cart-items');
        selectorToUse = '.page-width'; // Extract the inner content, not the full cart-items wrapper
      } else if (section.id === 'cart-drawer') {
        // Target the active/visible cart drawer (not hidden duplicates)
        const cartDrawers = document.querySelectorAll('cart-drawer');
        if (cartDrawers.length > 1) {
          // Find the visible/active cart drawer
          element = Array.from(cartDrawers).find(drawer => 
            !drawer.hasAttribute('hidden') && 
            drawer.offsetParent !== null && 
            window.getComputedStyle(drawer).display !== 'none'
          ) || cartDrawers[0]; // Fallback to first if none are clearly visible
        } else {
          element = cartDrawers[0];
        }
        selectorToUse = 'cart-drawer'; // Extract just the inner cart-drawer content, not the wrapper
      } else {
        element = section.selector 
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
      }
        
      if (element && parsedState.sections[section.id]) {
        const newHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id], 
          selectorToUse
        );
        
        elementUpdates.set(element, newHTML);
        
        if (window.cartPerformanceDebug) {
          console.log(`Prepared update for section ${section.id}`);
        }
      }
    });
    
    // Apply all updates in a single animation frame for better performance
    requestAnimationFrame(() => {
      const updateStart = performance.now();
      
      // ENHANCED ICR PRESERVATION: Save ICR upsells before DOM update
      const hiddenICRUpsells = new Set();
      const preservedICRUpsells = new Map(); // Store complete state of ICR upsells
      
      // Check for recently completed ICR requests that should trigger hiding
      if (window.cartPerformanceManager && window.cartPerformanceManager.getRecentICRCompletions) {
        const recentCompletions = window.cartPerformanceManager.getRecentICRCompletions();
        recentCompletions.forEach(completion => {
          if (completion.lineItemKey) {
            hiddenICRUpsells.add(completion.lineItemKey);
            if (window.cartPerformanceDebug) {
              console.log(`🎯 ICR-HIDE: Marking ${completion.lineItemKey} to be hidden due to recent ICR completion`);
            }
          }
        });
      }
      
      elementUpdates.forEach((html, element) => {
        // Check if this element contains cart items with ICR upsells that should stay hidden
        if (element.classList.contains('cart-drawer') || element.closest('cart-drawer') || 
            element.id === 'main-cart-items' || element.closest('cart-items') || 
            element.id === 'main-cart-footer' || element.closest('#main-cart-items')) {
          
          const existingUpsells = element.querySelectorAll('.line-item-icr-upsell');
          existingUpsells.forEach(upsell => {
            const lineItemKey = upsell.dataset.lineItemKey;
            if (lineItemKey) {
              // Store current state (hidden or visible)
              const isCurrentlyHidden = upsell.classList.contains('icr-optimistic-hiding') || 
                                      upsell.classList.contains('icr-adapter-present') ||
                                      upsell.style.opacity === '0' ||
                                      upsell.style.visibility === 'hidden';
              
              // Force hiding if this ICR upsell should be hidden due to recent completion
              const shouldBeHidden = isCurrentlyHidden || hiddenICRUpsells.has(lineItemKey);
              
              if (shouldBeHidden) {
                hiddenICRUpsells.add(lineItemKey);
              }
              
              // Store the complete state information
              preservedICRUpsells.set(lineItemKey, {
                html: upsell.outerHTML,
                isHidden: shouldBeHidden, // Use the final state that should be applied
                classes: Array.from(upsell.classList),
                style: {
                  opacity: upsell.style.opacity,
                  maxHeight: upsell.style.maxHeight,
                  visibility: upsell.style.visibility,
                  overflow: upsell.style.overflow,
                  transition: upsell.style.transition
                }
              });
              
              if (window.cartPerformanceDebug) {
                console.log(`💾 PRE-DOM: Preserving ICR upsell ${lineItemKey} (currently: ${isCurrentlyHidden ? 'hidden' : 'visible'}, final: ${shouldBeHidden ? 'hidden' : 'visible'})`);
                console.log(`   Classes: ${Array.from(upsell.classList).join(', ')}`);
                console.log(`   Style: opacity=${upsell.style.opacity}, visibility=${upsell.style.visibility}`);
              }
            }
          });
        }
      });
      
      // Apply DOM updates
      elementUpdates.forEach((html, element) => {
        element.innerHTML = html;
      });
      
      // ENHANCED ICR RESTORATION: Restore and hide ICR upsells after DOM update  
      if (preservedICRUpsells.size > 0) {
        preservedICRUpsells.forEach((preservedState, lineItemKey) => {
          let upsellFound = false;
          
          // Look in all updated elements for the ICR upsell
          elementUpdates.forEach((html, element) => {
            const upsellContainer = element.querySelector(`.line-item-icr-upsell[data-line-item-key="${lineItemKey}"]`);
            if (upsellContainer) {
              upsellFound = true;
              
              if (window.cartPerformanceDebug) {
                console.log(`🔄 RESTORE: Found ICR upsell ${lineItemKey} after DOM update`);
                console.log(`   Preserved state: hidden=${preservedState.isHidden}`);
              }
              
              // Restore preserved state
              if (preservedState.isHidden) {
                if (window.cartPerformanceDebug) {
                  console.log(`⚡ RESTORE-HIDE: Applying preserved hidden state to ICR upsell ${lineItemKey}`);
                }
                
                // Apply all preserved classes
                preservedState.classes.forEach(className => {
                  if (className && !upsellContainer.classList.contains(className)) {
                    upsellContainer.classList.add(className);
                  }
                });
                
                // Apply preserved styles with no transition to prevent flash
                upsellContainer.style.transition = 'none';
                if (preservedState.style.opacity) upsellContainer.style.opacity = preservedState.style.opacity;
                if (preservedState.style.maxHeight) upsellContainer.style.maxHeight = preservedState.style.maxHeight;
                if (preservedState.style.visibility) upsellContainer.style.visibility = preservedState.style.visibility;
                if (preservedState.style.overflow) upsellContainer.style.overflow = preservedState.style.overflow;
                
                // Restore transition after next frame
                requestAnimationFrame(() => {
                  if (upsellContainer && preservedState.style.transition) {
                    upsellContainer.style.transition = preservedState.style.transition;
                  } else if (upsellContainer) {
                    upsellContainer.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
                  }
                });
              } else {
                if (window.cartPerformanceDebug) {
                  console.log(`✅ RESTORE-SHOW: ICR upsell ${lineItemKey} was visible, keeping visible`);
                }
              }
            }
          });
          
          // If upsell not found, log this for debugging
          if (!upsellFound && window.cartPerformanceDebug) {
            console.warn(`⚠️ ICR upsell ${lineItemKey} was lost during section update - this may cause cart.js errors`);
          }
        });
      }
      
      // CRITICAL: Re-initialize ICR upsell after DOM updates and flash prevention
      // The innerHTML replacement destroys event listeners, so we need to re-attach them
      if (typeof window !== 'undefined' && window.document) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.initializeICRUpsell === 'function') {
          cartDrawer.initializeICRUpsell();
        }
        
        // Also re-initialize main cart ICR upsell if it exists
        const mainCartItems = document.querySelector('cart-items');
        if (mainCartItems && typeof mainCartItems.initializeICRUpsell === 'function') {
          mainCartItems.initializeICRUpsell();
        }
      }
      
      if (window.cartPerformanceDebug) {
        const updateEnd = performance.now();
        console.log(`DOM updates applied in ${updateEnd - updateStart}ms for ${elementUpdates.size} sections`);
        if (hiddenICRUpsells.size > 0) {
          console.log(`🚫 Flash prevention applied to ${hiddenICRUpsells.size} ICR upsell(s)`);
        }
      }
    });
  }

  /**
   * Optimized section HTML extraction using shared parser
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    try {
      const doc = this.parser.parseFromString(html, 'text/html');
      const element = doc.querySelector(selector);
      
      if (!element) {
        if (window.cartPerformanceDebug) {
          console.warn(`Cart Section Manager: Could not find element with selector "${selector}". Available elements:`, 
            Array.from(doc.body.children).map(el => el.tagName + (el.id ? `#${el.id}` : '') + (el.className ? `.${el.className.replace(/\s+/g, '.')}` : '')));
        }
        
        // Fallback: if cart-drawer selector fails, try .shopify-section
        if (selector === 'cart-drawer') {
          const fallbackElement = doc.querySelector('.shopify-section');
          if (fallbackElement) {
            if (window.cartPerformanceDebug) {
              console.log('Cart Section Manager: Using .shopify-section fallback for cart-drawer');
            }
            return fallbackElement.innerHTML;
          }
        }
        
        return '';
      }
      
      if (window.cartPerformanceDebug && selector === 'cart-drawer') {
        console.log('Cart Section Manager: Successfully extracted cart-drawer inner content, length:', element.innerHTML.length);
      }
      
      return element.innerHTML;
    } catch (error) {
      console.warn('Cart Section Manager: Failed to parse section HTML:', error);
      return '';
    }
  }

  /**
   * Generate a content hash for meaningful comparison
   * Ignores dynamic elements like timestamps, CSRF tokens, etc.
   */
  getContentHash(html, section) {
    try {
      // Extract meaningful content for comparison
      const doc = this.parser.parseFromString(html, 'text/html');
      const element = doc.querySelector(section.selector || '.shopify-section');
      
      if (!element) return html; // Fallback to full content if parsing fails
      
      // Remove or normalize dynamic content
      const contentElement = element.cloneNode(true);
      
      // Remove timestamps, CSRF tokens, and other dynamic content
      this.normalizeDynamicContent(contentElement);
      
      // Generate hash from normalized content
      return this.simpleHash(contentElement.innerHTML);
    } catch (error) {
      if (window.cartPerformanceDebug) {
        console.warn('Content hash generation failed, using full content:', error);
      }
      return this.simpleHash(html);
    }
  }

  /**
   * Remove or normalize dynamic content that changes on every request
   */
  normalizeDynamicContent(element) {
    // Remove or normalize common dynamic attributes
    const dynamicAttributes = ['data-timestamp', 'data-time', 'data-csrf', 'data-authenticity-token'];
    const dynamicSelectors = [
      '[data-timestamp]',
      '[data-time]', 
      '[name*="authenticity_token"]',
      '[value*="token"]',
      '.timestamp',
      '.time-stamp'
    ];
    
    // Remove dynamic attributes
    dynamicAttributes.forEach(attr => {
      const elements = element.querySelectorAll(`[${attr}]`);
      elements.forEach(el => el.removeAttribute(attr));
    });
    
    // Normalize dynamic elements
    dynamicSelectors.forEach(selector => {
      const elements = element.querySelectorAll(selector);
      elements.forEach(el => {
        // Replace with normalized placeholder
        if (el.tagName === 'INPUT') {
          el.value = 'NORMALIZED';
        } else {
          el.textContent = 'NORMALIZED';
        }
      });
    });
    
    // Normalize cart item keys which may change
    const cartKeys = element.querySelectorAll('[data-cart-item-key], [id*="CartItem-"], [id*="cart-item-"]');
    cartKeys.forEach(el => {
      if (el.id) {
        el.id = el.id.replace(/\d+/g, 'NORMALIZED');
      }
    });
    
    // CRITICAL: Normalize price values that change with quantity updates
    // This prevents the cart footer totals from being cached incorrectly
    const priceElements = element.querySelectorAll(
      '.totals__subtotal-value, .cart-item__price-wrapper, [class*="price"], ' +
      '.cart-item__totals, .price--end, .pn-total-line-item, ' +
      '[data-cart-total], [data-line-price]'
    );
    priceElements.forEach(el => {
      if (el.textContent && (el.textContent.includes('$') || el.textContent.includes('USD') || el.textContent.match(/\d+\.\d+/))) {
        el.textContent = 'PRICE_NORMALIZED';
      }
      // Also normalize data attributes that might contain prices
      if (el.dataset.cartTotal) el.dataset.cartTotal = 'NORMALIZED';
      if (el.dataset.linePrice) el.dataset.linePrice = 'NORMALIZED';
    });
  }

  /**
   * Simple hash function for content comparison
   */
  simpleHash(str) {
    let hash = 0;
    if (str.length === 0) return hash;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  }

  /**
   * Update cache with new section content and hash
   */
  updateSectionCache(parsedState, sectionsToRender) {
    sectionsToRender.forEach(section => {
      const sectionId = section.id;
      if (parsedState.sections[sectionId]) {
        const content = parsedState.sections[sectionId];
        const hash = this.getContentHash(content, section);
        
        this.sectionCache.set(sectionId, {
          content: content,
          hash: hash,
          timestamp: Date.now()
        });
        
        if (window.cartPerformanceDebug) {
          console.log(`Cached section ${sectionId} with hash: ${hash}`);
        }
      }
    });
  }

  /**
   * Invalidate cache for specific sections or all sections
   */
  invalidateCache(sectionIds = null) {
    if (sectionIds) {
      sectionIds.forEach(id => this.sectionCache.delete(id));
    } else {
      this.sectionCache.clear();
    }
  }

  /**
   * Check if cart state has changed significantly
   */
  hasCartStateChanged(newCartState) {
    if (!this.lastCartState) {
      this.lastCartState = newCartState;
      return true;
    }
    
    // Compare item count and total price for quick change detection
    const lastState = this.lastCartState;
    const changed = (
      lastState.item_count !== newCartState.item_count ||
      lastState.total_price !== newCartState.total_price ||
      lastState.items?.length !== newCartState.items?.length
    );
    
    if (changed) {
      this.lastCartState = newCartState;
    }
    
    return changed;
  }

  /**
   * Get performance metrics for debugging
   */
  getPerformanceMetrics() {
    return {
      ...this.performanceMetrics,
      cacheSize: this.sectionCache.size,
      cacheHitRatio: this.performanceMetrics.totalUpdates > 0 
        ? (this.performanceMetrics.cacheHits / this.performanceMetrics.totalUpdates * 100).toFixed(2) + '%'
        : '0%'
    };
  }
}

// Create global instance (singleton pattern)
if (!window.cartSectionManager) {
  window.cartSectionManager = new CartSectionManager();
}

// Enable performance debugging with: window.cartPerformanceDebug = true;

// Debugging utilities
window.cartSectionManager.showPerformanceStats = function() {
  const stats = window.cartSectionManager.getPerformanceMetrics();
  console.group('🚀 Cart Section Manager Performance Stats');
  console.log('📊 Total Updates:', stats.totalUpdates);
  console.log('⚡ Skipped Updates:', stats.skippedUpdates);
  console.log('🎯 Cache Hits:', stats.cacheHits);
  console.log('📈 Cache Hit Ratio:', stats.cacheHitRatio);
  console.log('💾 Cache Size:', stats.cacheSize, 'sections');
  
  if (stats.totalUpdates > 0) {
    const efficiency = ((stats.skippedUpdates + stats.cacheHits) / stats.totalUpdates * 100).toFixed(1);
    console.log('⚡ Efficiency:', efficiency + '%', '(updates avoided)');
  }
  
  console.log('🔧 Enable debug mode: window.cartPerformanceDebug = true');
  console.groupEnd();
};

// Debug cache contents
window.cartSectionManager.showCacheContents = function() {
  console.group('💾 Cart Section Cache Contents');
  if (window.cartSectionManager.sectionCache.size === 0) {
    console.log('Cache is empty');
  } else {
    window.cartSectionManager.sectionCache.forEach((data, sectionId) => {
      console.group(`Section: ${sectionId}`);
      console.log('Hash:', data.hash);
      console.log('Timestamp:', new Date(data.timestamp).toLocaleTimeString());
      console.log('Content length:', data.content.length, 'characters');
      console.log('Content preview:', data.content.substring(0, 200) + '...');
      console.groupEnd();
    });
  }
  console.groupEnd();
};

// Force cache clear for testing
window.cartSectionManager.clearCache = function() {
  window.cartSectionManager.invalidateCache();
  console.log('🗑️ Cache cleared');
};

// Debug flash prevention
window.cartSectionManager.testFlashPrevention = function() {
  console.group('🔍 Testing ICR Flash Prevention');
  
  const allUpsells = document.querySelectorAll('.line-item-icr-upsell');
  console.log(`Found ${allUpsells.length} ICR upsell container(s):`);
  
  allUpsells.forEach((upsell, index) => {
    const lineItemKey = upsell.dataset.lineItemKey;
    const isFlashHide = upsell.classList.contains('icr-flash-hide');
    const isOptimistic = upsell.classList.contains('icr-optimistic-hiding');
    const isAdapterPresent = upsell.classList.contains('icr-adapter-present');
    const opacity = upsell.style.opacity;
    const visibility = upsell.style.visibility;
    
    console.log(`  ${index + 1}. Line Item: ${lineItemKey}`);
    console.log(`     - Flash Hide: ${isFlashHide ? '✅ Yes' : '❌ No'}`);
    console.log(`     - Optimistic: ${isOptimistic ? '✅ Yes' : '❌ No'}`);
    console.log(`     - Adapter Present: ${isAdapterPresent ? '✅ Yes' : '❌ No'}`);
    console.log(`     - Opacity: ${opacity || 'default'}`);
    console.log(`     - Visibility: ${visibility || 'default'}`);
    console.log(`     - Visible: ${upsell.offsetHeight > 0 ? '✅ Yes' : '❌ No'}`);
  });
  
  console.groupEnd();
};

// Enable flash prevention debugging
window.cartSectionManager.enableFlashDebug = function() {
  window.cartPerformanceDebug = true;
  console.log('🔧 Flash prevention debugging enabled - you will see detailed logs during DOM updates');
  console.log('💡 Add an ICR adapter to see the flash prevention in action');
  console.log('🧪 Run window.cartSectionManager.testFlashPrevention() to check current state');
};

// Debug main cart page sections
window.cartSectionManager.debugMainCartSections = function() {
  console.group('🔍 Main Cart Page Sections Debug');
  
  const mainCartItems = document.getElementById('main-cart-items');
  const mainCartFooter = document.getElementById('main-cart-footer');
  
  console.log('📋 Main Cart Elements:');
  console.log('  - main-cart-items:', mainCartItems ? '✅ Found' : '❌ Missing');
  console.log('  - main-cart-footer:', mainCartFooter ? '✅ Found' : '❌ Missing');
  
  if (mainCartItems) {
    const icrUpsells = mainCartItems.querySelectorAll('.line-item-icr-upsell');
    console.log(`  - ICR Upsells in items: ${icrUpsells.length}`);
    
    // Check for different content selectors
    const jsContents = mainCartItems.querySelector('.js-contents');
    const cartItems = mainCartItems.querySelector('.cart__items');
    const titleElements = mainCartItems.querySelectorAll('.title--primary, h1');
    
    console.log(`  - .js-contents found: ${!!jsContents}`);
    console.log(`  - .cart__items found: ${!!cartItems}`);
    console.log(`  - Title elements: ${titleElements.length}`);
    
    if (jsContents) {
      console.log(`  - .js-contents innerHTML length: ${jsContents.innerHTML.length}`);
    }
    
    icrUpsells.forEach((upsell, index) => {
      console.log(`    ${index + 1}. Line Item: ${upsell.dataset.lineItemKey}`);
      console.log(`       - Product ID: ${upsell.dataset.productId}`);
      console.log(`       - Visible: ${upsell.offsetHeight > 0 ? '✅ Yes' : '❌ No'}`);
    });
  }
  
  if (mainCartFooter) {
    const totalElements = mainCartFooter.querySelectorAll('[class*="total"], [class*="price"]');
    console.log(`  - Total/Price elements in footer: ${totalElements.length}`);
  }
  
  console.log('\n🧪 Test Commands:');
  console.log('  - Test cart update: window.cartPerformanceManager.testCartSectionUpdate()');
  console.log('  - Test flash prevention: window.cartSectionManager.testFlashPrevention()');
  console.log('  - Test content extraction: window.cartSectionManager.testContentExtraction()');
  
  console.groupEnd();
};

// Test content extraction
window.cartSectionManager.testContentExtraction = function() {
  console.group('🧪 Testing Content Extraction');
  
  const mainCartItems = document.getElementById('main-cart-items');
  if (!mainCartItems) {
    console.log('❌ main-cart-items not found');
    console.groupEnd();
    return;
  }
  
  // Test different selectors for main-cart-items
  const itemsSelectors = ['.js-contents', '.cart__items', '.page-width'];
  
  console.log('🎯 Testing main-cart-items selectors:');
  itemsSelectors.forEach(selector => {
    const element = mainCartItems.querySelector(selector);
    if (element) {
      console.log(`✅ ${selector}:`);
      console.log(`  - Element:`, element);
      console.log(`  - Content length: ${element.innerHTML.length}`);
      console.log(`  - Title elements inside: ${element.querySelectorAll('.title--primary, h1').length}`);
      console.log(`  - ICR upsells inside: ${element.querySelectorAll('.line-item-icr-upsell').length}`);
    } else {
      console.log(`❌ ${selector}: Not found`);
    }
  });
  
  // Test footer selectors
  const mainCartFooter = document.getElementById('main-cart-footer');
  if (mainCartFooter) {
    console.log('\n🎯 Testing main-cart-footer selectors:');
    const footerSelectors = ['.shopify-section', '.page-width', 'div'];
    
    footerSelectors.forEach(selector => {
      const element = mainCartFooter.querySelector(selector);
      if (element) {
        console.log(`✅ footer ${selector}:`);
        console.log(`  - Element:`, element);
        console.log(`  - Content length: ${element.innerHTML.length}`);
        console.log(`  - Visible: ${element.offsetHeight > 0}`);
      } else {
        console.log(`❌ footer ${selector}: Not found`);
      }
    });
    
    console.log(`\n📋 Footer direct content length: ${mainCartFooter.innerHTML.length}`);
    console.log(`📋 Footer visible: ${mainCartFooter.offsetHeight > 0}`);
  }
  
  console.groupEnd();
};

// Debug footer content extraction specifically
window.cartSectionManager.debugFooterExtraction = function() {
  console.group('🔍 DEBUG: Footer Content Extraction');
  
  const mainCartFooter = document.getElementById('main-cart-footer');
  if (!mainCartFooter) {
    console.log('❌ main-cart-footer not found');
    console.groupEnd();
    return;
  }
  
  console.log('📋 Footer Analysis:');
  console.log('  - Footer element:', mainCartFooter);
  console.log('  - Current visibility:', mainCartFooter.offsetHeight > 0 ? '✅ Visible' : '❌ Hidden');
  console.log('  - Current content length:', mainCartFooter.innerHTML.length);
  
  // Test extraction with different selectors without actually updating
  const testSelectors = ['.shopify-section', '.page-width', 'div', '> *'];
  
  testSelectors.forEach(selector => {
    try {
      const extracted = window.cartSectionManager.getSectionInnerHTML(
        mainCartFooter.outerHTML,
        selector
      );
      
      console.log(`\n🧪 Selector "${selector}":`);
      console.log(`  - Extracted length: ${extracted.length}`);
      console.log(`  - Contains checkout button: ${extracted.includes('checkout') || extracted.includes('Checkout')}`);
      console.log(`  - Contains total: ${extracted.includes('total') || extracted.includes('Total')}`);
      
      if (extracted.length > 0 && extracted.length < 500) {
        console.log(`  - Preview: ${extracted.substring(0, 200)}...`);
      }
    } catch (error) {
      console.log(`❌ Selector "${selector}" failed:`, error.message);
    }
  });
  
  // Test what happens if we use the whole footer content
  console.log(`\n🧪 Using entire footer innerHTML:`);
  console.log(`  - Length: ${mainCartFooter.innerHTML.length}`);
  console.log(`  - Contains checkout: ${mainCartFooter.innerHTML.includes('checkout')}`);
  
  console.log('\n💡 Recommendations:');
  console.log('  - Use selector that preserves checkout button and totals');
  console.log('  - Avoid selectors that return empty content');
  console.log('  - Test: window.cartSectionManager.testFooterUpdate()');
  
  console.groupEnd();
};

// Test footer update safely
window.cartSectionManager.testFooterUpdate = function() {
  console.group('🧪 Testing Footer Update Safely');
  
  const mainCartFooter = document.getElementById('main-cart-footer');
  if (!mainCartFooter) {
    console.log('❌ main-cart-footer not found');
    console.groupEnd();
    return;
  }
  
  // Save current state
  const originalHTML = mainCartFooter.innerHTML;
  const originalVisibility = mainCartFooter.offsetHeight > 0;
  
  console.log('💾 Saved current footer state');
  console.log(`  - Original visibility: ${originalVisibility ? '✅ Visible' : '❌ Hidden'}`);
  console.log(`  - Original content length: ${originalHTML.length}`);
  
  // Test with a safe selector (just return current content)
  const testContent = window.cartSectionManager.getSectionInnerHTML(
    mainCartFooter.outerHTML,
    '.page-width'
  );
  
  if (testContent && testContent.length > 0) {
    console.log('🔄 Testing footer content replacement...');
    
    // Apply test content
    mainCartFooter.innerHTML = testContent;
    
    setTimeout(() => {
      const newVisibility = mainCartFooter.offsetHeight > 0;
      console.log(`  - New visibility: ${newVisibility ? '✅ Visible' : '❌ Hidden'}`);
      console.log(`  - New content length: ${mainCartFooter.innerHTML.length}`);
      
      if (!newVisibility || mainCartFooter.innerHTML.length < originalHTML.length * 0.5) {
        console.warn('⚠️ Footer update caused issues - restoring original');
        mainCartFooter.innerHTML = originalHTML;
      } else {
        console.log('✅ Footer update looks good');
      }
      
      console.groupEnd();
    }, 100);
  } else {
    console.error('❌ Test content extraction failed');
    console.groupEnd();
  }
};

// Check current cart totals
window.cartSectionManager.debugCartTotals = function() {
  console.group('💰 DEBUG: Cart Totals Analysis');
  
  // Check all possible total elements
  const totalElements = [
    document.querySelector('.totals__subtotal-value'),
    document.querySelector('[class*="total"]'),
    document.querySelector('.cart-total'),
    ...document.querySelectorAll('[class*="subtotal"]')
  ].filter(el => el);
  
  console.log(`📊 Found ${totalElements.length} total element(s):`);
  
  totalElements.forEach((element, index) => {
    console.log(`  ${index + 1}. ${element.className}:`);
    console.log(`     - Text: "${element.textContent.trim()}"`);
    console.log(`     - Parent: ${element.parentElement?.className || 'none'}`);
    console.log(`     - In footer: ${element.closest('#main-cart-footer') ? '✅ Yes' : '❌ No'}`);
  });
  
  // Check cart state from form
  const cartForm = document.querySelector('#cart');
  if (cartForm) {
    const formData = new FormData(cartForm);
    const quantities = [];
    for (const [key, value] of formData.entries()) {
      if (key === 'updates[]') {
        quantities.push(value);
      }
    }
    console.log(`🛒 Cart form quantities: [${quantities.join(', ')}]`);
  }
  
  console.groupEnd();
};

// Debug ICR preservation during section updates
window.cartSectionManager.debugICRPreservation = function() {
  console.group('🔍 DEBUG: ICR Preservation Status');
  
  const mainCartItems = document.getElementById('main-cart-items');
  
  // Target the active/visible cart drawer (not hidden duplicates)
  const cartDrawers = document.querySelectorAll('cart-drawer');
  let cartDrawer = null;
  
  if (cartDrawers.length > 1) {
    // Find the visible/active cart drawer
    cartDrawer = Array.from(cartDrawers).find(drawer => 
      !drawer.hasAttribute('hidden') && 
      drawer.offsetParent !== null && 
      window.getComputedStyle(drawer).display !== 'none'
    ) || cartDrawers[0]; // Fallback to first if none are clearly visible
  } else {
    cartDrawer = cartDrawers[0];
  }
  
  console.log('📋 ICR Upsell Analysis:');
  
  [
    { name: 'Main Cart', element: mainCartItems },
    { name: 'Cart Drawer', element: cartDrawer }
  ].forEach(({ name, element }) => {
    if (!element) {
      console.log(`❌ ${name}: Element not found`);
      return;
    }
    
    const allUpsells = element.querySelectorAll('.line-item-icr-upsell');
    const hiddenUpsells = element.querySelectorAll('.line-item-icr-upsell.icr-adapter-present, .line-item-icr-upsell.icr-optimistic-hiding');
    const visibleUpsells = Array.from(allUpsells).filter(u => !u.classList.contains('icr-adapter-present') && !u.classList.contains('icr-optimistic-hiding'));
    
    console.log(`🎯 ${name}:`);
    console.log(`  - Total ICR upsells: ${allUpsells.length}`);
    console.log(`  - Hidden upsells: ${hiddenUpsells.length}`);
    console.log(`  - Visible upsells: ${visibleUpsells.length}`);
    
    allUpsells.forEach((upsell, index) => {
      const lineItemKey = upsell.dataset.lineItemKey;
      const productId = upsell.dataset.productId;
      const isHidden = upsell.classList.contains('icr-adapter-present') || upsell.classList.contains('icr-optimistic-hiding');
      
      console.log(`    ${index + 1}. ${lineItemKey}:`);
      console.log(`       - Product ID: ${productId}`);
      console.log(`       - Hidden: ${isHidden ? '✅' : '❌'}`);
      console.log(`       - Classes: ${upsell.className}`);
    });
  });
  
  console.log('🧪 Test ICR preservation by running:');
  console.log('   window.cartPerformanceManager.testCartSectionUpdate()');
  
  console.groupEnd();
};

// Auto-log performance stats when debug is enabled
if (window.cartPerformanceDebug) {
  setInterval(() => {
    window.cartSectionManager.showPerformanceStats();
  }, 10000); // Show stats every 10 seconds
} 